import { PersonEmploymentTypeBadge, PersonStatusBadge } from "@/components/persons/person-badges";
import type { Adorn } from "@/components/persons/person-profile/use-pending-adornments";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import type { PersonDetail } from "@/hooks/use-persons";
import { formatDateOnly } from "@/lib/dates";

export function EmploymentCard({
  person,
  adorn,
  canView,
}: {
  person: PersonDetail;
  adorn: Adorn;
  canView: boolean;
}) {
  return (
    <DetailSection title="Employment" boldTitle className="gap-3 py-4">
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <MetadataRow label="Job title" value={person.title} adornment={adorn("title")} />
        <MetadataRow label="Employment Type" adornment={adorn("employmentType")}>
          <PersonEmploymentTypeBadge employmentType={person.employmentType} />
        </MetadataRow>
        <MetadataRow label="Status" adornment={adorn("status")}>
          <PersonStatusBadge status={person.status} />
        </MetadataRow>
        {person.employmentType === "contractor" && (
          <MetadataRow
            label="Entity name"
            value={person.entityName}
            adornment={adorn("entityName")}
          />
        )}
        <MetadataRow
          label="Start date"
          value={formatDateOnly(person.startDate)}
          adornment={adorn("startDate")}
        />
        {/* Rehire / End dates only render when set (or a change is queued) —
            empty for most people, so no em-dash placeholder. */}
        {(person.rehireDate || adorn("rehireDate")) && (
          <MetadataRow
            label="Rehire date"
            value={formatDateOnly(person.rehireDate)}
            adornment={adorn("rehireDate")}
          />
        )}
        {(person.endDate || adorn("endDate")) && (
          <MetadataRow
            label="End date"
            value={formatDateOnly(person.endDate)}
            adornment={adorn("endDate")}
          />
        )}
        {/* Variable Compensation start date — admin tier only, on every profile
            INCLUDING your own (see `ADMIN_ONLY_PERSON_FIELDS`). The `canView`
            check is the second layer: the read routes redact the value to null
            for anyone below that tier, so this row is already empty for them —
            gating it here keeps a queued-change icon from appearing against a
            value they can't see. Otherwise it follows the rehire/end pattern of
            rendering only when set. */}
        {canView && (person.vcStartDate || adorn("vcStartDate")) && (
          <MetadataRow
            label="VC Start Date"
            value={formatDateOnly(person.vcStartDate)}
            adornment={adorn("vcStartDate")}
          />
        )}
      </div>
    </DetailSection>
  );
}
